import { useCallback, useMemo, useState } from "react";
import { Avatar, Button, Loader, Text } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import type {
  SpotlightActionData,
  SpotlightActionGroupData,
} from "@mantine/spotlight";
import { Spotlight, spotlight } from "@mantine/spotlight";
import "./SearchBar.scss";
import ArchiveModal from "@components/Modals/ArchiveModal/ArchiveModal";
import { useClientNavigation } from "@/utils/hooks/useClientNavigation";

interface SearchClient {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
  status?: string; // Status field from the backend search results
  difficulty?: number;
}

interface SearchBarProps {
  data: SearchClient[] | [];
  isLoading?: boolean;
  isClient?: boolean;
}

const SearchBar = ({ data, isLoading, isClient = true }: SearchBarProps) => {
  const [archivedModalOpen, setArchivedModalOpen] = useState(false);
  const [selectedArchivedClient, setSelectedArchivedClient] =
    useState<SearchClient | null>(null);

  const { navigateToClient } = useClientNavigation();

  const goToItem = useCallback(
    (client: SearchClient) => {
      if (!client?.userId) return; // Add guard clause

      navigateToClient(client, {
        onArchived: (archivedClient) => {
          setSelectedArchivedClient(archivedClient);
          setArchivedModalOpen(true);
          spotlight.close();
        },
      });
    },
    [navigateToClient]
  );

  /**
   * Converts a team role to a human-readable description.
   * @param role - The team role
   * @returns The human-readable description
   */
  const getRoleDescription = useCallback((role: string) => {
    const teamRole = role.toLocaleLowerCase();
    if (teamRole === "owner") {
      return "Owner";
    } else if (teamRole === "team_member") {
      return "Team Member";
    } else if (teamRole === "team_lead") {
      return "Team Lead";
    } else {
      return "";
    }
  }, []);

  // Memoize spotlight actions to prevent recreation on every render
  const actions = useMemo((): (
    SpotlightActionData | SpotlightActionGroupData
  )[] => {
    return data.map((client: SearchClient, index: number) => ({
      key: client.userId || index,
      group: "",
      actions: [
        {
          id: client.userId || index.toString(),
          label: `${client.firstName} ${client.lastName}`,
          description: client.email,
          leftSection: (
            <Avatar color="orange" variant="filled" size="md">
              {client.firstName?.[0] || ""}
              {client.lastName?.[0] || ""}
            </Avatar>
          ),
          rightSection: (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {client.status === "Archived" && (
                <Text size="sm" fw={500} c="dimmed">
                  Archived
                </Text>
              )}
              {getRoleDescription(client?.role ?? "")}
            </div>
          ),
          onClick: isClient ? () => goToItem(client) : undefined,
          disabled: !isClient,
          style: { cursor: `${isClient ? "pointer" : "default"}` },
        },
      ],
    }));
  }, [data, isClient, goToItem, getRoleDescription]);

  let searchMessage = "Search for a teammate";

  if (isClient) {
    searchMessage = "Search for a client";
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <div className="search-bar-container">
        <Button
          leftSection={<IconSearch />}
          onClick={spotlight.open}
          className="search-button"
        >
          {searchMessage}
        </Button>
        <Spotlight
          className="spotlight"
          actions={actions}
          nothingFound="Nothing found..."
          scrollable
          maxHeight={620}
          size={600}
          searchProps={{
            leftSection: <IconSearch stroke={1.5} className="search-icon" />,
            placeholder: "Search...",
          }}
        />
      </div>

      <ArchiveModal
        opened={archivedModalOpen}
        onClose={() => {
          setArchivedModalOpen(false);
          setSelectedArchivedClient(null);
        }}
        clientName={
          selectedArchivedClient
            ? `${selectedArchivedClient.firstName} ${selectedArchivedClient.lastName}`
            : ""
        }
      />
    </>
  );
};

export default SearchBar;
